import { useQueryGetEnabledFeatures } from "../data/services/featureAccessService";

/**
 * Does an enabled-features response contain this key?
 *
 * Shared with useAnyClientFeatureAccess so both gates fail safe the same way:
 * false while loading, on error, and on an unexpected response shape - a broken
 * lookup hides the gated feature rather than exposing it to everyone.
 *
 * Comparison is case-insensitive. Keys are stored lowercase, but normalizing
 * both sides means a call site that passes "NewRenewalDashboard" still resolves
 * instead of silently never opening the gate.
 */
export const hasEnabledFeature = (
  { data, isLoading, isError },
  featureKey
) => {
  if (isLoading || isError || !featureKey) {
    return false;
  }

  const enabledKeys = data?.data;

  if (!Array.isArray(enabledKeys)) {
    return false;
  }

  const wanted = String(featureKey).trim().toLowerCase();

  return enabledKeys.some(
    (key) => String(key).trim().toLowerCase() === wanted
  );
};

/**
 * Is a gated feature enabled for this client and OE period?
 *
 * Opt-in semantics: only clients on the Feature Access Control allow-list get
 * the feature.
 */
const useFeatureAccess = (featureKey, oePeriodId, clientId) => {
  const result = useQueryGetEnabledFeatures(oePeriodId, clientId);

  return hasEnabledFeature(result, featureKey);
};

export default useFeatureAccess;
